import { StateService } from '@uirouter/angular';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FrsPackageService } from '../services/frs.package.service';
import { UiService } from '../../../core/services/core.ui.service';
import { ServiceResponseProcessor } from '../../../core/services/core.response-processor.service';
import { PackageModelService } from '../services/frs.package.model.service';
import { ModalComponent } from '../../../core/config/core.modal-helpers';
import { FrsReleaseService } from '../services/frs.release.service';
import { NotificationService } from '../../../core/services/core.notification.service';
import { ReleaseModel } from '../services/frs.release.model.service';
import { HttpClient } from '@angular/common/http';
import { UploadService } from '../../../core/services/core.upload.service';

import * as _ from 'lodash';
import { doGetPromise } from '../../../core/config/core.httpclient-utils';

@Component({
    selector: 'frs-release-edit',
    template: require('./frs.release.edit.html')
})
export class FrsReleaseEditComponent extends ModalComponent
    implements OnInit, OnDestroy {
    @Input() project: any = null;
    @Input() release: any = null;
    @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();

    private forceReload = false;
    protected draggingOver = false;
    protected filesUploading: any = null;

    protected showDeleteConfirmation = false;
    protected maxUploadSize = 0;

    protected formErrors: any = {};
    protected fetchingChangelog = false;
    protected changelogItems: any = false;

    protected yesNo = {
        'Yes': 'Y',
        'No': 'N'
    };

    protected visibleHidden = {
        'Visible': 'Y',
        'Hidden': 'N'
    };

    constructor(
        private stateService: StateService,
        private frsReleaseService: FrsReleaseService,
        private frsPackageService: FrsPackageService,
        private uiService: UiService,
        private serviceResponseProcessor: ServiceResponseProcessor,
        private notificationService: NotificationService,
        protected releaseModel: ReleaseModel,
        private packageModel: PackageModelService,
        private http: HttpClient,
        private $window: Window,
        private uploadService: UploadService,
    ) { super(); }

    ngOnInit(): void {

        this.draggingOver = false;
        this.forceReload = false;

        this.releaseModel.data = _.clone(this.release);

        // KLUDGE - in case the release was previously saved with null values.
        this.valueOrDefault(this.releaseModel, 'data.releaseNotes');
        this.valueOrDefault(this.releaseModel, 'data.changes');

        this.filesUploading = [];

        this.notificationService.subscribe('frs-release', 'DRAGOVER_START_EVENT', () => {
            if (this.draggingOver !== true) {
                this.draggingOver = true;
            }
        });

        this.notificationService.subscribe('frs-release', 'DRAGOVER_END_EVENT', () => {
            this.draggingOver = false;
        });

        this.releaseModel.resetErrors();
        this.frsPackageService.fetch(this.project.id)
            .then((result) => {

                var projectPackagesWithAdmin = _.filter(result.items, (p) => {
                    return this.packageModel.acl.isAdmin(p);
                });
                var projectPackagesOptions = projectPackagesWithAdmin.map((p) => {
                    return { id: p.id, label: p.packageName, defaultSelected: false };
                });
                if (projectPackagesOptions.length > 0) {
                    this.releaseModel.data.package = { id: projectPackagesOptions[0].id };
                }
                this.releaseModel.packageOptions.fieldElements = projectPackagesOptions;

            });

        this.maxUploadSize = this.uploadService.getMaxUploadSize('frs');
    }

    ngOnDestroy(): void {
        this.notificationService.unsubscribe('frs-release');
    }

    uploadFileToFrs = (section, refId, file, hash) => {
        var params = {
            hash: hash,
            fileName: file.name,
            fileType: file.type
        };

        this.forceReload = true;

        return this.frsReleaseService.saveFile(this.release.id, params)
            .then((response: any) => {
                if (!this.releaseModel.data.files) {
                    this.releaseModel.data.files = [];
                }
                if (response && response.file) {
                    this.releaseModel.data.rel.files.push(response.file);
                }
            }, () => { }
        );
    };

    /**
     * Private method. This function adds an array of file objects to an array
     * of files that are currently being uploaded. This array is then used for
     * displaying progress
     * 
     * @param {object[]} files 
     */
    addFilesToUploading(files) {
        _.map(files, (file) => {
            this.filesUploading.push({
                name: file.name,
                size: file.size,
                bytesUploaded: 0,
                bytesRemaining: file.size,
                percentageUploaded: 0,
                percentageRemaining: 100
            });
        });
    }

    removeFileFromInProgress = (section, refId, file) => {
        _.remove(this.filesUploading, { name: file.name });
    };

    /**
     * Private. Callback method for file progress updates
     * 
     * @param {string} section 
     * @param {int} refId 
     * @param {object} file 
     * @param {int} bytesUploaded 
     * @param {int} bytesRemaining 
     * @param {int} percentUploaded 
     * @param {int} percentRemaining 
     */
    progressCallback = (section, refId, file, bytesUploaded, bytesRemaining, percentUploaded, percentRemaining) => {
        var fileInProgress = _.find(this.filesUploading, { name: file.name });

        if (!_.isEmpty(fileInProgress)) {
            fileInProgress.amountComplete = bytesUploaded;
            fileInProgress.amountTotal = bytesRemaining;
            fileInProgress.percentComplete = percentUploaded;
            fileInProgress.percentRemaining = percentRemaining;
        }
    };

    setConfigValue(path, value) {
        if (value == undefined || value == _.get(this, path)) return false;
        _.set(this, path, value);
    };

    save() {
        this.formErrors = {};
        this.showDeleteConfirmation = false;

        var releaseUpdated = _.clone(this.releaseModel);
        releaseUpdated.data.package = { id: this.releaseModel.data.package.id };
        releaseUpdated.data.preformatted = this.releaseModel.data.preformatted == 'Y' ? 'Y' : 'N';

        delete releaseUpdated.data.files;
        delete releaseUpdated.data.user;
        this.frsReleaseService.save(releaseUpdated.data.id, releaseUpdated.data)
            .then((response) => {
                this.uiService.message.showSuccess('Release sucessfully saved.');
                // The save already succeeded at this point, so any error raised while
                // notifying listeners (e.g. a subscriber to `close` throwing) must not
                // be reported as a save failure via the error toast below.
                try {
                    this.close.emit(true);
                } catch (error) {
                    console.error(error);
                }
            }, (response) => {
                this.uiService.message.showError("There was an error trying to save the release");
                this.formErrors = this.serviceResponseProcessor.process(response.error, {}, this.releaseModel.errorTypes);
            });
    };

    deleteRelease(release) {
        this.frsReleaseService.delete(release.id)
            .then((response) => {
                this.uiService.message.showSuccess('Release sucessfully deleted.');
                this.close.emit(true);
            }, (response) => {
                this.uiService.message.showError("There was an error trying to delete the release");
                this.showDeleteConfirmation = false;
            }
            ).catch((error) => {
                this.uiService.message.showError("There was an error trying to delete the release");
                this.showDeleteConfirmation = false;
                throw error;
            });
    };

    onFileDrop($event: any) {
        $event.preventDefault();
        this.draggingOver = false;
        if ($event.dataTransfer && $event.dataTransfer.files) {
            this.processFileUploads($event.dataTransfer.files);
        }
    }

    fileSelected($event) {
        const input = $event.target as HTMLInputElement;

        if (input.files && input.files.length > 0) {
            this.processFileUploads(input.files);
        }

    }

    processFileUploads(files: any) {
        if ((files === undefined) || files.length == 0) {
            return;
        }

        //Todo: enable this call when file uploads in progress view is ready
        this.addFilesToUploading(files);

        var options = {
            successCallback: this.uploadFileToFrs,
            progressCallback: this.progressCallback,
            finishedCallback: this.removeFileFromInProgress
        };

        this.uploadService.uploadFiles('frsrelease', this.release.id, files, options)
            .then(() => { })
            .catch(() => { });
    };

    deleteReleaseFile(file) {
        return this.frsReleaseService.deleteFile(this.release.id, file.id)
            .then(() => {
                _.remove(this.releaseModel.data.rel.files, { id: file.id });
            }, () => {
            });
    };

    importChangelog() {
        this.fetchingChangelog = true;
        var url = "/api/trackeritem?page_num=1&page_size=500&release=" + this.release.id + "&sort_dir=desc&sort_field=priority&project=" + this.stateService.params.unixName;
        doGetPromise(this.http, url)
            .then(
                (response) => {
                    this.changelogItems = _.get(response, 'items', []);
                    this.$window.setTimeout(
                        () => {
                            var content = $('#changelog-content').html();
                            this.releaseModel.data.changes += content;
                            this.changelogItems = false;
                        }, 0
                    );
                },
                () => {
                    this.uiService.message.showWarning("Sorry, could not fetch the change log for this Release", "Import Changelog");
                }
            ).finally(() => {
                this.fetchingChangelog = false;
            });
    };

    setVisibility(value: any) {
        this.releaseModel.data.isVisible = value;
    };

    setPackage(id: any) {
        _.set(this.releaseModel, 'data.package.id', id);
    };

    onUpdateReleaseDate($event) {
        this.releaseModel.data.releaseDate = $event;
    }

    valueOrDefault(obj, path, def = '') {
        const v = _.get(obj, path, null);
        if (! v) {
            _.set(obj, path, def);
        }
        return obj;
    }
};
